import { useEffect, useRef } from 'react';
import { WabaChannelStatus, WabaIntegration } from '../typings/waba';

export interface UseWabaInitialSelectionParams {
  wabaIntegrationsData: WabaIntegration[] | undefined;
  setSelectedWaba: (waba: WabaIntegration) => void;
  enabled?: boolean; // Whether to run the selection logic
  allowedIntegrationIds?: string[] | null;
  userProfile?: { primaryIntegration?: WabaIntegration | null } | null;
  onChange?: (waba: WabaIntegration) => void;
  onSingleWabaSubmit?: () => void;
  skipWhenSelected?: WabaIntegration | null;
  runOnlyOnce?: boolean;
}

export function useWabaInitialSelection({
  wabaIntegrationsData,
  setSelectedWaba,
  enabled = true,
  allowedIntegrationIds,
  userProfile,
  onChange,
  onSingleWabaSubmit,
  skipWhenSelected,
  runOnlyOnce = true,
}: UseWabaInitialSelectionParams): void {
  const initialSet = useRef(false);

  useEffect(() => {
    if (enabled && wabaIntegrationsData?.length && !skipWhenSelected && !(runOnlyOnce && initialSet.current)) {
      const data = wabaIntegrationsData!;
      const allowed = (waba: WabaIntegration) =>
        !allowedIntegrationIds?.length || (allowedIntegrationIds && allowedIntegrationIds.includes(waba.id));

      const apply = (waba: WabaIntegration) => {
        setSelectedWaba(waba);
        onChange?.(waba);
        if (data.length === 1) onSingleWabaSubmit?.();
        if (runOnlyOnce) initialSet.current = true;
      };

      let wabaToApply: WabaIntegration | null = null;

      const fromStorage = localStorage.getItem('selectedWaba');
      if (fromStorage) {
        try {
          const parsed = JSON.parse(fromStorage);
          const found = data.find((w) => w.id === parsed.id && allowed(w));
          if (found?.status === WabaChannelStatus.ACTIVE) {
            localStorage.setItem('selectedWaba', JSON.stringify(found));
            wabaToApply = found;
          }
        } catch {
          /* ignore */
        }
      }

      if (!wabaToApply && userProfile?.primaryIntegration?.status === WabaChannelStatus.ACTIVE) {
        wabaToApply = userProfile.primaryIntegration!;
      }

      if (!wabaToApply) {
        for (const waba of data) {
          if (waba.status === WabaChannelStatus.ACTIVE && allowed(waba)) {
            wabaToApply = waba.isSelfAssigned ? waba : wabaToApply ?? waba;
            if (waba.isSelfAssigned) break;
          }
        }
        wabaToApply = wabaToApply ?? data[0];
      }

      apply(wabaToApply);
    }
  }, [
    enabled,
    wabaIntegrationsData,
    skipWhenSelected,
    runOnlyOnce,
    setSelectedWaba,
    onChange,
    onSingleWabaSubmit,
    userProfile?.primaryIntegration?.id,
    allowedIntegrationIds,
  ]);
}
